import { AbsoluteFill, interpolate, useCurrentFrame } from "remotion";
import {
  Caption,
  Callout,
  CodePanel,
  COLORS,
  FONTS,
  MetricCounter,
  Panel,
  PipelineGraph,
  SceneTransition,
  SlideTimeBar,
  type PipeNode,
} from "../../coldcompute";
import { SCENE } from "./constants";
import { HOOK, PIPELINE, CAUSE, FIX, MEMORY, CONSTANTS, PAYOFF, OUTRO } from "./script";

const center: React.CSSProperties = {
  alignItems: "center",
  justifyContent: "center",
  flexDirection: "column",
};

const Kicker: React.FC<{ text: string; color: string }> = ({ text, color }) => (
  <div
    style={{
      fontFamily: FONTS.mono,
      fontSize: 22,
      letterSpacing: "0.22em",
      textTransform: "uppercase",
      color,
      marginBottom: 22,
    }}
  >
    ▍{text}
  </div>
);

const Big: React.FC<{ children: React.ReactNode; size?: number }> = ({
  children,
  size = 92,
}) => (
  <div
    style={{
      fontFamily: FONTS.display,
      fontWeight: 700,
      fontSize: size,
      lineHeight: 0.98,
      letterSpacing: "-0.02em",
      color: COLORS.text,
      textAlign: "center",
      maxWidth: 1520,
    }}
  >
    {children}
  </div>
);

/* 01 — HOOK : every sync died at exactly 20% */
export const Scene01Hook: React.FC = () => {
  const frame = useCurrentFrame();
  const pct = Math.round(
    interpolate(frame, [10, 55], [0, 20], {
      extrapolateLeft: "clamp",
      extrapolateRight: "clamp",
    }),
  );
  const die = interpolate(frame, [56, 70], [0, 1], {
    extrapolateLeft: "clamp",
    extrapolateRight: "clamp",
  });
  return (
    <AbsoluteFill style={center}>
      <SlideTimeBar sceneDurationFrames={SCENE.hook.duration} />
      <SceneTransition from="up">
        <AbsoluteFill style={center}>
          <Kicker text="sync engine · what broke" color={COLORS.cyan} />
          <Big>
            every sync died at exactly{" "}
            <span style={{ color: die > 0.5 ? COLORS.red : COLORS.text }}>{pct}%</span>
          </Big>
          {/* a thin progress rail frozen at 20% */}
          <div
            style={{
              marginTop: 40,
              width: 900,
              height: 14,
              background: COLORS.panel,
              border: `1px solid ${COLORS.line}`,
              borderRadius: 8,
              overflow: "hidden",
            }}
          >
            <div
              style={{
                width: `${pct}%`,
                height: "100%",
                background: die > 0.5 ? COLORS.red : COLORS.cyan,
              }}
            />
          </div>
          <div
            style={{
              fontFamily: FONTS.mono,
              fontSize: 24,
              color: COLORS.dim,
              marginTop: 22,
              opacity: die,
            }}
          >
            no stack trace · no error · just death
          </div>
        </AbsoluteFill>
      </SceneTransition>
      <Caption lines={HOOK} accent="red" />
    </AbsoluteFill>
  );
};

/* 02 — PIPELINE : the 5-step worker; step 3 is the cliff */
const STEPS: PipeNode[] = [
  { label: "0 → 20%", value: "FETCH", sub: "github tree API", accent: "cyan" },
  { label: "dedup", value: "SHA-256", sub: "skip unchanged", accent: "green" },
  { label: "20 → 70%", value: "PROCESS", sub: "chunk · embed · store", accent: "red" },
  { label: "70 → 95%", value: "ENTITIES", sub: "knowledge graph", accent: "violet" },
  { label: "→ 100%", value: "FINALIZE", sub: "cache · audit", accent: "amber" },
];
export const Scene02Pipeline: React.FC = () => (
  <AbsoluteFill style={center}>
    <SlideTimeBar sceneDurationFrames={SCENE.pipeline.duration} />
    <AbsoluteFill style={{ ...center, padding: "0 70px" }}>
      <Kicker text="src/lib/jobs/sync-worker.ts" color={COLORS.cyan} />
      <div style={{ width: 1740 }}>
        <PipelineGraph nodes={STEPS} startFrame={8} stepFrames={20} nodeWidth={250} />
      </div>
      <div
        style={{
          fontFamily: FONTS.body,
          fontSize: 28,
          color: COLORS.dim,
          marginTop: 44,
          textAlign: "center",
        }}
      >
        Step 3 holds ~80% of the work — and{" "}
        <span style={{ color: COLORS.red }}>that is where it crashed.</span>
      </div>
    </AbsoluteFill>
    <Caption lines={PIPELINE} accent="cyan" />
  </AbsoluteFill>
);

/* 03 — CAUSE : the mutual recursion */
const CYCLE = `// adaptive-chunker.ts — the cycle that crashed every sync
chunkMarkdownStructured(bigSection)
  ──▶ section too large, fall back to general
        chunkGeneralStructured(section)
          ──▶ "looks like markdown" (has headings)
                chunkMarkdownStructured(section)
                  ──▶ still too large…
                        chunkGeneralStructured(section)
                          ──▶ … ∞  →  STACK OVERFLOW`;
export const Scene03Cause: React.FC = () => (
  <AbsoluteFill style={center}>
    <SlideTimeBar sceneDurationFrames={SCENE.cause.duration} />
    <AbsoluteFill style={{ ...center, paddingBottom: 30 }}>
      <Kicker text="the real bug · mutual recursion" color={COLORS.red} />
      <CodePanel
        code={CYCLE}
        language="javascript"
        filename="adaptive-chunker.ts — call cycle"
        revealFrames={260}
        fontSize={26}
        width={1320}
      />
    </AbsoluteFill>
    <Caption lines={CAUSE} accent="red" />
  </AbsoluteFill>
);

/* 04 — FIX : the one-line change */
const FIX_CODE = `// chunkMarkdownStructured() — adaptive-chunker.ts
for (const section of sections) {
  if (section.length > MAX_CHUNK_SIZE) {
    // Use chunkGeneral directly — NOT chunkGeneralStructured —
    // to avoid mutual recursion → stack overflow.
    const plainChunks = await chunkGeneral(section)  // ← the fix
    chunks.push(...plainChunks)
  } else {
    chunks.push(section)
  }
}`;
export const Scene04Fix: React.FC = () => (
  <AbsoluteFill style={center}>
    <SlideTimeBar sceneDurationFrames={SCENE.fix.duration} />
    <AbsoluteFill style={{ ...center, paddingBottom: 30 }}>
      <Kicker text="the fix · one line" color={COLORS.green} />
      <CodePanel
        code={FIX_CODE}
        language="typescript"
        filename="adaptive-chunker.ts"
        highlightLines={[6]}
        revealFrames={230}
        fontSize={25}
        width={1300}
      />
    </AbsoluteFill>
    <Caption lines={FIX} accent="green" />
  </AbsoluteFill>
);

/* 05 — MEMORY : for-loop vs .map() + Semaphore */
export const Scene05Memory: React.FC = () => {
  const frame = useCurrentFrame();
  const reveal = interpolate(frame, [40, 75], [0, 1], {
    extrapolateLeft: "clamp",
    extrapolateRight: "clamp",
  });
  return (
    <AbsoluteFill style={center}>
      <SlideTimeBar sceneDurationFrames={SCENE.memory.duration} />
      <AbsoluteFill style={center}>
        <Kicker text="why the VPS OOMed" color={COLORS.amber} />
        <div style={{ display: "flex", gap: 30, alignItems: "stretch" }}>
          <Panel accent="red" title="before · .map() + Semaphore" width={560}>
            <div style={{ fontFamily: FONTS.mono, fontSize: 20, lineHeight: 1.85, color: COLORS.text }}>
              <div style={{ color: COLORS.dim }}>concurrency = 1, yet…</div>
              <div>docs.map(async d =&gt; …)</div>
              <div style={{ color: COLORS.red }}>670+ promises allocated at once</div>
              <div style={{ color: COLORS.dim }}>each captures doc + idx → heap</div>
            </div>
          </Panel>
          <div style={{ display: "flex", alignItems: "center", fontFamily: FONTS.display, fontSize: 52, color: COLORS.dim }}>
            →
          </div>
          <Panel accent="green" title="after · plain for-loop" width={560}>
            <div style={{ fontFamily: FONTS.mono, fontSize: 20, lineHeight: 1.85, color: COLORS.text, opacity: reveal }}>
              <div>for (let i = 0; i &lt; docs.length; i++)</div>
              <div style={{ color: COLORS.green }}>one promise at a time</div>
              <div style={{ color: COLORS.green }}>one document in memory</div>
              <div style={{ color: COLORS.dim }}>memory stays flat</div>
            </div>
          </Panel>
        </div>
        <div style={{ fontFamily: FONTS.body, fontSize: 26, color: COLORS.dim, marginTop: 34 }}>
          a Semaphore gates execution — but not allocation.
        </div>
      </AbsoluteFill>
      <Caption lines={MEMORY} accent="amber" />
    </AbsoluteFill>
  );
};

/* 06 — CONSTANTS : every number earned from a failure (LIVE values) */
const ROWS: { k: string; tried: string; now: string; accent: string }[] = [
  { k: "EMBED_BATCH_SIZE", tried: "32 → timed out", now: "16", accent: COLORS.cyan },
  { k: "DOC_CONCURRENCY", tried: "5 → rate-limit cascade", now: "1 (serial)", accent: COLORS.green },
  { k: "INSERT_BATCH_SIZE", tried: "1 per chunk", now: "50", accent: COLORS.lime },
  { k: "DELETE_BATCH_SIZE", tried: "200 → URL limit", now: "25", accent: COLORS.amber },
  { k: "keepAlive", tried: "true → ECONNRESET", now: "false", accent: COLORS.magenta },
  { k: "maxSockets", tried: "∞ → pool starved", now: "4", accent: COLORS.violet },
];
export const Scene06Constants: React.FC = () => {
  const frame = useCurrentFrame();
  return (
    <AbsoluteFill style={center}>
      <SlideTimeBar sceneDurationFrames={SCENE.constants.duration} />
      <AbsoluteFill style={center}>
        <Kicker text="every magic number = a production failure" color={COLORS.amber} />
        <div style={{ display: "flex", flexDirection: "column", gap: 12, width: 1280 }}>
          {ROWS.map((r, i) => {
            const op = interpolate(frame, [10 + i * 12, 28 + i * 12], [0, 1], {
              extrapolateLeft: "clamp",
              extrapolateRight: "clamp",
            });
            return (
              <div
                key={r.k}
                style={{
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "space-between",
                  background: COLORS.panel,
                  border: `1px solid ${COLORS.line}`,
                  borderLeft: `3px solid ${r.accent}`,
                  borderRadius: 8,
                  padding: "14px 22px",
                  opacity: op,
                  transform: `translateY(${(1 - op) * 12}px)`,
                }}
              >
                <span style={{ fontFamily: FONTS.mono, fontSize: 24, color: COLORS.text, flex: "0 0 360px" }}>{r.k}</span>
                <span style={{ fontFamily: FONTS.mono, fontSize: 20, color: COLORS.dim, flex: "1 1 auto" }}>{r.tried}</span>
                <span style={{ fontFamily: FONTS.display, fontWeight: 700, fontSize: 28, color: r.accent, flex: "0 0 180px", textAlign: "right" }}>{r.now}</span>
              </div>
            );
          })}
        </div>
      </AbsoluteFill>
      <Caption lines={CONSTANTS} accent="amber" />
    </AbsoluteFill>
  );
};

/* 07 — PAYOFF : 670+ files → 2,110 chunks, flat memory */
export const Scene07Payoff: React.FC = () => {
  const frame = useCurrentFrame();
  const c1 = interpolate(frame, [150, 180], [0, 1], { extrapolateLeft: "clamp", extrapolateRight: "clamp" });
  const c2 = interpolate(frame, [220, 250], [0, 1], { extrapolateLeft: "clamp", extrapolateRight: "clamp" });
  return (
    <AbsoluteFill style={center}>
      <SlideTimeBar sceneDurationFrames={SCENE.payoff.duration} />
      <AbsoluteFill style={center}>
        <Kicker text="a real sync · EAKC repo" color={COLORS.green} />
        <div style={{ display: "flex", alignItems: "baseline", gap: 26 }}>
          <span style={{ fontFamily: FONTS.display, fontWeight: 700, fontSize: 70, color: COLORS.dim }}>670+ files</span>
          <span style={{ fontFamily: FONTS.display, fontSize: 60, color: COLORS.dim }}>→</span>
          <MetricCounter value={2110} from={0} startFrame={10} durationFrames={64} accent="green" fontSize={150} format={(n) => `${Math.round(n).toLocaleString()}`} />
          <span style={{ fontFamily: FONTS.display, fontWeight: 600, fontSize: 44, color: COLORS.text, marginLeft: 8 }}>chunks</span>
        </div>
        <div style={{ display: "flex", gap: 30, marginTop: 40 }}>
          <div style={{ opacity: c1 }}>
            <Callout label="in-process · Dokploy VPS" accent="cyan" width={380}>
              sync runs inside <b style={{ color: COLORS.text }}>Next.js</b> — no worker box
            </Callout>
          </div>
          <div style={{ opacity: c2 }}>
            <Callout label="memory profile" accent="lime" width={380}>
              <b style={{ color: COLORS.text }}>flat</b> — one doc loaded, freed, repeat
            </Callout>
          </div>
        </div>
      </AbsoluteFill>
      <Caption lines={PAYOFF} accent="green" />
    </AbsoluteFill>
  );
};

/* 08 — OUTRO */
export const Scene08Outro: React.FC = () => (
  <AbsoluteFill style={center}>
    <SlideTimeBar sceneDurationFrames={SCENE.outro.duration} />
    <SceneTransition from="up">
      <AbsoluteFill style={center}>
        <Big size={66}>
          A for-loop instead of map + Semaphore.
          <br />
          A plain splitter instead of a "smart" one.
          <br />
          <span style={{ color: COLORS.cyan }}>Complexity was the enemy.</span>
        </Big>
        <div style={{ fontFamily: FONTS.mono, fontSize: 24, letterSpacing: "0.24em", color: COLORS.dim, marginTop: 44 }}>
          EAKC · sync engine
        </div>
      </AbsoluteFill>
    </SceneTransition>
    <Caption lines={OUTRO} accent="cyan" />
  </AbsoluteFill>
);

;
    var _remotion_globalVariableA;
    var _remotion_globalVariableB;
    // Legacy CSS implementations will `eval` browser code in a Node.js context
    // to extract CSS. For backwards compatibility, we need to check we're in a
    // browser context before continuing.
    if (typeof self !== 'undefined' &&
        // AMP / No-JS mode does not inject these helpers:
        '$RefreshHelpers$' in self) {
        const currentExports = __webpack_module__.exports;
        const prevExports = (_remotion_globalVariableB = (_remotion_globalVariableA = __webpack_module__.hot.data) === null || _remotion_globalVariableA === void 0 ? void 0 : _remotion_globalVariableA.prevExports) !== null && _remotion_globalVariableB !== void 0 ? _remotion_globalVariableB : null;
        // This cannot happen in MainTemplate because the exports mismatch between
        // templating and execution.
        self.$RefreshHelpers$.registerExportsForReactRefresh(currentExports, __webpack_module__.id);
        // A module can be accepted automatically based on its exports, e.g. when
        // it is a Refresh Boundary.
        if (self.$RefreshHelpers$.isReactRefreshBoundary(currentExports)) {
            // Save the previous exports on update so we can compare the boundary
            // signatures.
            __webpack_module__.hot.dispose((data) => {
                data.prevExports = currentExports;
            });
            // Unconditionally accept an update to this module, we'll check if it's
            // still a Refresh Boundary later.
            __webpack_module__.hot.accept();
            // This field is set when the previous version of this module was a
            // Refresh Boundary, letting us know we need to check for invalidation or
            // enqueue an update.
            if (prevExports !== null) {
                // A boundary can become ineligible if its exports are incompatible
                // with the previous exports.
                //
                // For example, if you add/remove/change exports, we'll want to
                // re-execute the importing modules, and force those components to
                // re-render. Similarly, if you convert a class component to a
                // function, we want to invalidate the boundary.
                if (self.$RefreshHelpers$.shouldInvalidateReactRefreshBoundary(prevExports, currentExports)) {
                    __webpack_module__.hot.invalidate();
                }
                else {
                    self.$RefreshHelpers$.scheduleUpdate();
                }
            }
        }
        else {
            // Since we just executed the code for the module, it's possible that the
            // new exports made it ineligible for being a boundary.
            // We only care about the case when we were _previously_ a boundary,
            // because we already accepted this update (accidental side effect).
            const isNoLongerABoundary = prevExports !== null;
            if (isNoLongerABoundary) {
                __webpack_module__.hot.invalidate();
            }
        }
    }
